import React, { useState } from 'react';
import type { User, Household, HouseholdRole, MealType, Tenant, DietTypes } from '../types';
import UsersIcon from './icons/UsersIcon';
import PlusIcon from './icons/PlusIcon';
import TrashIcon from './icons/TrashIcon';
import CogIcon from './icons/CogIcon';
import SparklesIcon from './icons/SparklesIcon';
import HouseholdIcon from './icons/HouseholdIcon';
import ChevronUpIcon from './icons/ChevronUpIcon';
import ChevronDownIcon from './icons/ChevronDownIcon';
import { getTranslation } from '../utils/translations';
import { canUserCreateHousehold, getUserHouseholdLimit } from '../utils/userHouseholdLimit';
import { getConsultantSwitchableHouseholds, isGuidedClientHousehold, canAccessGuidedClientsAdmin } from '../utils/guidedClientHousehold';
import {
    buildResidentsUpdate,
    getHouseholdAdultsCount,
    getHouseholdChildrenCount,
} from '../utils/householdResidents';
import CopiedNoticeModal from './CopiedNoticeModal';
import SmartUnitCapsule from './SmartUnitCapsule';
import ConfirmationModal from './ConfirmationModal';
import InfoNoticeModal from './InfoNoticeModal';
import DietTypesSelector from './DietTypesSelector';
import KnownAllergiesSelector from './KnownAllergiesSelector';
import { EMPTY_DIET_TYPES } from '../utils/dietTypes';
import { type KnownAllergyId, ALLERGY_CONFIG } from '../utils/allergies';
import { db } from '../services/firebase';
import { collection, doc, onSnapshot, query, where } from 'firebase/firestore';
import { createTempUserForHousehold, deleteTempUserAccess, refreshGuidedClientAccess } from '../services/guidedClientService';
import type { GuidedClient, GuidedClientAccess } from '../types';

interface HouseholdManagementModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentUser: User;
    activeHouseholdId?: string;
    allUsers: User[];
    households: Household[];
    onCreateHousehold: (name: string, visibleMeals?: MealType[], adults?: number, children?: number) => void;
    onAddMember: (householdId: string, email: string) => void;
    onRemoveMember: (householdId: string, userId: string) => void;
    onUpdateMemberRole: (householdId: string, userId: string, role: HouseholdRole) => void;
    onDeleteHousehold: (householdId: string) => void;
    onUpdateHouseholdConfig: (householdId: string, updates: Partial<Household>) => void;
    onSwitchHousehold: (householdId: string) => void;
}

const InformationCircleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" {...props}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
    </svg>
);

const HouseholdManagementModal: React.FC<HouseholdManagementModalProps> = ({ 
    isOpen, 
    onClose, 
    currentUser, 
    activeHouseholdId,
    allUsers,
    households,
    onCreateHousehold,
    onAddMember,
    onRemoveMember,
    onUpdateMemberRole,
    onDeleteHousehold,
    onUpdateHouseholdConfig,
    onSwitchHousehold
}) => {
    // Household Creation States
    const [newHouseholdName, setNewHouseholdName] = useState('');
    const [newAdultsCount, setNewAdultsCount] = useState(1);
    const [newChildrenCount, setNewChildrenCount] = useState(0);
    const [isCreateHouseholdOpen, setIsCreateHouseholdOpen] = useState(false);
    const [newVisibleMeals, setNewVisibleMeals] = useState<{Breakfast: boolean, Lunch: boolean, Dinner: boolean}>({
        Breakfast: true, Lunch: true, Dinner: true
    });

    // Sub-Modal Control States
    const [editingHouseholdId, setEditingHouseholdId] = useState<string | null>(null);
    const [activeTenantForm, setActiveTenantForm] = useState<{ householdId: string; tenant?: Tenant } | null>(null);

    // Dialog Notice & Confirmation States
    const [infoNotice, setInfoNotice] = useState<{ title: string; message: string; variant?: 'info' | 'success' | 'error' } | null>(null);
    const [confirmAction, setConfirmAction] = useState<{ title: string; message: string; onConfirm: () => void } | null>(null);

    // Collapsible Lists Tracking States
    const [expandedTenantIds, setExpandedTenantIds] = useState<Record<string, boolean>>({});
    const [expandedMemberIds, setExpandedMemberIds] = useState<Record<string, boolean>>({});
    const [isInvitedExpanded, setIsInvitedExpanded] = useState<Record<string, boolean>>({});
    const [isRejectedExpanded, setIsRejectedExpanded] = useState<Record<string, boolean>>({});
    const [isTenantsExpanded, setIsTenantsExpanded] = useState<Record<string, boolean>>({});

    // Settings Modal Inputs
    const [inviteEmailInput, setInviteEmailInput] = useState('');
    const [editingNames, setEditingNames] = useState<Record<string, string>>({});
    const [showCopiedNotice, setShowCopiedNotice] = useState(false);
    const [viewingDetails, setViewingDetails] = useState<Household | null>(null);

    // Tenant Config Modal States
    const [tenantNameInput, setTenantNameInput] = useState('');
    const [tenantIsChild, setTenantIsChild] = useState(false);
    const [tenantDietTypes, setTenantDietTypes] = useState<DietTypes>({ ...EMPTY_DIET_TYPES });
    const [tenantAllergies, setTenantAllergies] = useState<KnownAllergyId[]>([]);
    const [isFormDietOpen, setIsFormDietOpen] = useState(false);
    const [isFormAllergiesOpen, setIsFormAllergiesOpen] = useState(false);

    // Temporary Users States
    const [isTempUsersExpanded, setIsTempUsersExpanded] = useState<Record<string, boolean>>({});
    const [tempEmail, setTempEmail] = useState('');
    const [tempFirstName, setTempFirstName] = useState('');
    const [tempLastName, setTempLastName] = useState('');
    const [tempLoading, setTempLoading] = useState(false);
    const [tempError, setTempError] = useState('');
    const [tempAccessRows, setTempAccessRows] = useState<GuidedClientAccess[]>([]);
    const [tempClients, setTempClients] = useState<Record<string, GuidedClient>>({});

    // Subscribe to temporary users access and details for the active editing household
    React.useEffect(() => {
        if (!isOpen || !editingHouseholdId) {
            setTempAccessRows([]);
            setTempClients({});
            return;
        }

        const qAccess = query(
            collection(db, 'guidedClientAccess'),
            where('householdId', '==', editingHouseholdId)
        );

        const unsubAccess = onSnapshot(qAccess, (snap) => {
            const rows = snap.docs.map((d) => ({ ...(d.data() as Omit<GuidedClientAccess, 'id'>), id: d.id }));
            setTempAccessRows(rows);
        }, (err) => {
            console.error('Failed to subscribe to temp user access', err);
        });

        return () => {
            unsubAccess();
        };
    }, [isOpen, editingHouseholdId]);

    React.useEffect(() => {
        if (tempAccessRows.length === 0) {
            setTempClients({});
            return;
        }

        const unsubs = tempAccessRows.map(row => 
            onSnapshot(doc(db, 'guidedClients', row.clientId), (snap) => {
                if (snap.exists()) {
                    setTempClients(prev => ({
                        ...prev,
                        [row.clientId]: { ...(snap.data() as Omit<GuidedClient, 'id'>), id: snap.id }
                    }));
                }
            }, (err) => {
                console.error('Failed to subscribe to temp client doc', err);
            })
        );

        return () => {
            unsubs.forEach(unsub => unsub());
        };
    }, [tempAccessRows]);

    const handleAddTempUser = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tempEmail.trim() || !tempFirstName.trim() || !editingHouseholdId) return;
        setTempLoading(true);
        setTempError('');
        try {
            const result = await createTempUserForHousehold({
                email: tempEmail.trim(),
                firstName: tempFirstName.trim(),
                lastName: tempLastName.trim() || undefined,
                householdId: editingHouseholdId,
            });
            setTempEmail('');
            setTempFirstName('');
            setTempLastName('');
            setInfoNotice({
                title: t('tempUserAdded') || 'Temporary user access link created!',
                message: result.accessUrl,
                variant: 'success'
            });
        } catch (err) {
            setTempError(err instanceof Error ? err.message : 'Failed to add temporary user');
        } finally {
            setTempLoading(false);
        }
    };

    const handleDeleteTempUser = async (accessId: string, clientId: string) => {
        if (!editingHouseholdId) return;
        try {
            await deleteTempUserAccess(editingHouseholdId, accessId);
            
            // If they had logged in and are in the household members, remove them from the household as well
            const client = tempClients[clientId];
            const household = households.find(hh => hh.id === editingHouseholdId);
            if (client?.userUid && household?.members.includes(client.userUid)) {
                await onRemoveMember(editingHouseholdId, client.userUid);
            }
        } catch (err) {
            setInfoNotice({
                title: t('error') || 'Error',
                message: err instanceof Error ? err.message : 'Failed to delete temporary user access'
            });
        }
    };

    const handleRefreshTempLink = async (accessId: string) => {
        try {
            const result = await refreshGuidedClientAccess(accessId);
            setInfoNotice({
                title: t('tempUserAdded') || 'Temporary user access link created!',
                message: result.accessUrl,
                variant: 'success'
            });
        } catch (err) {
            setInfoNotice({
                title: t('error') || 'Error',
                message: err instanceof Error ? err.message : 'Failed to refresh temporary user access link'
            });
        }
    };

    const t = (key: any) => getTranslation(currentUser.language, key);

    // Relocated conditional early return to prevent React hook violations
    if (!isOpen) return null;

    const hasPersonalDetails = !!(currentUser.nickname || (currentUser.firstName && currentUser.lastName));

    const ownedHouseholds = households.filter(
        (household) => household.ownerId === currentUser.uid && !isGuidedClientHousehold(household),
    );
    const myHouseholds = getConsultantSwitchableHouseholds(currentUser, households)
        .filter((household) => household.members.includes(currentUser.uid) || currentUser.role === 'superuser');
    const limit = getUserHouseholdLimit(currentUser);
    const hasReachedLimit = !canUserCreateHousehold(currentUser, ownedHouseholds.length);

    const resetCreateForm = () => {
        setNewHouseholdName('');
        setNewAdultsCount(1);
        setNewChildrenCount(0);
        setNewVisibleMeals({ Breakfast: true, Lunch: true, Dinner: true });
    };

    const handleCreate = (e: React.FormEvent) => {
        e.preventDefault();
        const selectedMeals: MealType[] = [];
        if (newVisibleMeals.Breakfast) selectedMeals.push('Breakfast');
        if (newVisibleMeals.Lunch) selectedMeals.push('Lunch');
        if (newVisibleMeals.Dinner) selectedMeals.push('Dinner');

        if (selectedMeals.length === 0) {
            setInfoNotice({ title: t('mustSelectOneMeal'), message: '' });
            return;
        }

        if (newHouseholdName.trim()) {
            onCreateHousehold(newHouseholdName.trim(), selectedMeals, newAdultsCount, newChildrenCount);
            resetCreateForm();
            setIsCreateHouseholdOpen(false);
        }
    };

    const handleInviteMember = (householdId: string) => {
        if (inviteEmailInput.trim()) {
            onAddMember(householdId, inviteEmailInput.trim());
            setInviteEmailInput('');
        }
    };

    const handleCancelInvitation = (hhId: string, targetUid: string) => {
        const hh = households.find(h => h.id === hhId);
        if (!hh) return;
        const updatedInvited = (hh.invitedMembers || []).filter(uid => uid !== targetUid);
        onUpdateHouseholdConfig(hhId, { invitedMembers: updatedInvited });
    };

    const handleClearRejected = (hhId: string, targetUid: string) => {
        const hh = households.find(h => h.id === hhId);
        if (!hh) return;
        const updatedRejected = { ...(hh.rejectedMembers || {}) };
        delete updatedRejected[targetUid];
        onUpdateHouseholdConfig(hhId, { rejectedMembers: updatedRejected });
    };

    const toggleMealSetting = (household: Household, meal: MealType) => {
        const rawMeals = household.visibleMealTypes || ['Lunch', 'Dinner'];
        const currentMeals: MealType[] = Array.isArray(rawMeals) 
            ? rawMeals 
            : (Object.keys(rawMeals) as MealType[]).filter(k => (rawMeals as any)[k] === true);
            
        let newMeals: MealType[];
        if (currentMeals.includes(meal)) {
            newMeals = currentMeals.filter(m => m !== meal);
        } else {
            const allMeals: MealType[] = ['Breakfast', 'Lunch', 'Dinner'];
            newMeals = allMeals.filter(m => currentMeals.includes(m) || m === meal);
        }

        if (newMeals.length === 0) {
            setInfoNotice({ title: t('cannotDisableAllMeals'), message: '' });
            return;
        }
        onUpdateHouseholdConfig(household.id, { visibleMealTypes: newMeals });
    };

    const handleResidentsChange = (householdId: string, adults: number, children: number) => {
        onUpdateHouseholdConfig(householdId, buildResidentsUpdate(adults, children));
    };

    const formatResidentsSummary = (household: Household) => {
        const adults = getHouseholdAdultsCount(household);
        const children = getHouseholdChildrenCount(household);
        return `${adults} ${t('adultsCount')}${children > 0 ? ` • ${children} ${t('childrenCount')}` : ''}`;
    };

    const getHouseholdName = (household: Household) => editingNames[household.id] ?? household.name;

    const handleHouseholdNameChange = (householdId: string, name: string) => {
        setEditingNames(prev => ({ ...prev, [householdId]: name }));
    };

    const handleHouseholdNameSave = (household: Household) => {
        const nextName = (editingNames[household.id] ?? household.name).trim();
        if (!nextName || nextName === household.name) {
            setEditingNames(prev => {
                const updated = { ...prev };
                delete updated[household.id];
                return updated;
            });
            return;
        }
        onUpdateHouseholdConfig(household.id, { name: nextName });
        setEditingNames(prev => {
            const updated = { ...prev };
            delete updated[household.id];
            return updated;
        });
    };

    const tRole = (role: HouseholdRole) => t(`${role}Role` as 'ownerRole');

    const getMyRole = (household: Household): HouseholdRole => {
        if (currentUser.role === 'superuser') return 'owner';
        if (isGuidedClientHousehold(household) && canAccessGuidedClientsAdmin(currentUser)) return 'owner';
        if (!household.memberRoles) return household.ownerId === currentUser.uid ? 'owner' : 'viewer';
        return household.memberRoles[currentUser.uid] || 'viewer';
    };

    const canManageMembers = (role: HouseholdRole) => ['owner', 'admin'].includes(role);
    const canChangeRoles = (role: HouseholdRole) => role === 'owner';
    const isPrimaryHousehold = (household: Household) => household.id === `hh-${currentUser.uid}`;

    // Tenant action triggers
    const handleAddTenantClick = (hhId: string) => {
        setTenantNameInput('');
        setTenantIsChild(false);
        setTenantDietTypes({ ...EMPTY_DIET_TYPES });
        setTenantAllergies([]);
        setIsFormDietOpen(false);
        setIsFormAllergiesOpen(false);
        setActiveTenantForm({ householdId: hhId });
    };

    const handleEditTenantClick = (hhId: string, tenant: Tenant) => {
        setTenantNameInput(tenant.name);
        setTenantIsChild(tenant.isChild === true);
        setTenantDietTypes({ ...EMPTY_DIET_TYPES, ...(tenant.dietTypes || {}) });
        setTenantAllergies(tenant.knownAllergies || []);
        setIsFormDietOpen(false);
        setIsFormAllergiesOpen(false);
        setActiveTenantForm({ householdId: hhId, tenant });
    };

    const handleSaveTenant = (hhId: string) => {
        if (!tenantNameInput.trim()) return;
        const hh = households.find(h => h.id === hhId);
        if (!hh) return;

        const currentTenants = hh.tenants || [];
        let updatedTenants: Tenant[];

        if (activeTenantForm?.tenant) {
            const editingTntId = activeTenantForm.tenant.id;
            updatedTenants = currentTenants.map(tnt => {
                if (tnt.id === editingTntId) {
                    return {
                        id: tnt.id,
                        name: tenantNameInput.trim(),
                        dietTypes: tenantDietTypes,
                        knownAllergies: tenantAllergies,
                        isChild: tenantIsChild
                    };
                }
                return tnt;
            });
        } else {
            const newTenant: Tenant = {
                id: Math.random().toString(36).substring(2, 9),
                name: tenantNameInput.trim(),
                dietTypes: tenantDietTypes,
                knownAllergies: tenantAllergies,
                isChild: tenantIsChild
            };
            updatedTenants = [...currentTenants, newTenant];
        }

        // Calculate and sync residents counts automatically
        const updatedHh = { ...hh, tenants: updatedTenants } as Household;
        const finalAdults = getHouseholdAdultsCount(updatedHh);
        const finalChildren = getHouseholdChildrenCount(updatedHh);
        
        onUpdateHouseholdConfig(hhId, {
            tenants: updatedTenants,
            adultsCount: finalAdults,
            childrenCount: finalChildren,
            residentsCount: finalAdults + finalChildren
        });
        
        setActiveTenantForm(null);
    };

    const handleDeleteTenant = (hhId: string, tenantId: string) => {
        const hh = households.find(h => h.id === hhId);
        if (!hh) return;
        const updatedTenants = (hh.tenants || []).filter(t => t.id !== tenantId);
        
        const updatedHh = { ...hh, tenants: updatedTenants } as Household;
        const finalAdults = getHouseholdAdultsCount(updatedHh);
        const finalChildren = getHouseholdChildrenCount(updatedHh);

        onUpdateHouseholdConfig(hhId, {
            tenants: updatedTenants,
            adultsCount: finalAdults,
            childrenCount: finalChildren,
            residentsCount: finalAdults + finalChildren
        });
    };

    const currentEditingHousehold = editingHouseholdId ? households.find(h => h.id === editingHouseholdId) : null;
    const editingRole = currentEditingHousehold ? getMyRole(currentEditingHousehold) : 'viewer';

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col relative overflow-hidden transition-colors animate-fade-in">
                {/* Modal Header */}
                <div className="p-6 border-b flex justify-between items-center bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700">
                    <div className="flex items-center gap-3">
                        <div className="bg-sky-100 dark:bg-sky-900/30 p-2 rounded-xl text-sky-600 dark:text-sky-400">
                            <HouseholdIcon className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="flex items-center gap-1.5">
                                <h2 className="text-xl font-black text-slate-800 dark:text-slate-100 tracking-tight">{t('manageHouseholdsTitle')}</h2>
                                <button
                                    type="button"
                                    onClick={() => {
                                        let message = '';
                                        if (canAccessGuidedClientsAdmin(currentUser)) {
                                            message += t('guidedClientsHouseholdHint') + '\n\n';
                                        }
                                        if (!hasPersonalDetails) {
                                            message += t('personalDetailsWarning') + '\n\n';
                                        }
                                        if (hasReachedLimit) {
                                            message += t('householdLimitDesc').replace('{limit}', limit.toString()) + '\n\n';
                                        }
                                        if (!message) {
                                            message = t('householdInfo') || 'Household management info';
                                        }
                                        setInfoNotice({
                                            title: t('notice') || 'Info',
                                            message: message.trim()
                                        });
                                    }}
                                    className="text-slate-400 hover:text-sky-600 transition-colors p-1"
                                    title={t('notice') || 'Info'}
                                >
                                    <InformationCircleIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                                {t('joinedHouseholds')} {myHouseholds.length}
                                {' · '}
                                {ownedHouseholds.length}/{limit}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!hasReachedLimit && (
                            <button
                                type="button"
                                onClick={() => setIsCreateHouseholdOpen(true)}
                                className={`flex items-center gap-1.5 px-4 py-1.5 bg-brand-orange text-white rounded-full shadow-md shadow-brand-orange/20 hover:bg-orange-600 active:scale-95 transition-all text-xs font-black uppercase tracking-widest border border-white/10 ${!hasPersonalDetails ? 'opacity-50 pointer-events-none' : ''}`}
                            >
                                <PlusIcon className="w-4 h-4" />
                                <span>{t('create')}</span>
                            </button>
                        )}
                        <button onClick={onClose} className="text-slate-400 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition-colors p-2 text-2xl font-bold" aria-label="Close dialog">&times;</button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto flex-grow bg-slate-50/50 dark:bg-slate-800/30">

                    {/* Households Section */}
                    {myHouseholds.some(h => isGuidedClientHousehold(h)) && (
                        <div className="mb-6">
                            <h3 className="font-black text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-3 px-1">
                                {t('guidedClientsHeader') || 'Guided Clients'}
                            </h3>
                            <div className="space-y-3">
                                {myHouseholds.filter(h => isGuidedClientHousehold(h)).map(household => {
                                    const myRole = getMyRole(household);
                                    const isActive = activeHouseholdId === household.id;
                                    return (
                                        <div 
                                            key={household.id} 
                                            onClick={() => setEditingHouseholdId(household.id)}
                                            className={`bg-white dark:bg-slate-900 border transition-all duration-200 rounded-2xl shadow-sm overflow-hidden p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-center justify-between gap-3 ${isActive ? 'border-emerald-300 dark:border-emerald-700 ring-2 ring-emerald-50 dark:ring-emerald-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                                        >
                                            <div className="flex gap-3 min-w-0 items-center">
                                                <div className={`w-1 rounded-full self-stretch min-h-[2.5rem] bg-emerald-500`} />
                                                <div className="min-w-0">
                                                    <h4 className="font-black text-slate-800 dark:text-slate-100 text-base leading-tight truncate">
                                                        {getHouseholdName(household)}
                                                    </h4>
                                                    <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                        <span className="text-[9px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 px-2 py-0.5 rounded-full font-black uppercase tracking-tighter">
                                                            {t('clientBadge') || 'Guided Client'}
                                                        </span>
                                                        <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold">
                                                            {household.members.length} {t('members')} • {formatResidentsSummary(household)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                                {isActive ? (
                                                    <span className="px-3 py-1.5 bg-emerald-600 dark:bg-emerald-700 text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                                                        {t('status_active')}
                                                    </span>
                                                ) : (
                                                    <button 
                                                        onClick={() => onSwitchHousehold(household.id)}
                                                        className="px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-600 transition-all shadow-sm active:scale-95"
                                                    >
                                                        {t('select')}
                                                    </button>
                                                )}
                                                <button 
                                                    onClick={() => setViewingDetails(household)}
                                                    className="p-2 text-slate-300 dark:text-slate-600 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-full transition-all"
                                                >
                                                    <InformationCircleIcon className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    <h3 className="font-black text-xs uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-4 px-1">
                        {t('myHouseholds')}
                    </h3>
                    <div className="space-y-3">
                        {myHouseholds.filter(h => !isGuidedClientHousehold(h)).map(household => {
                            const myRole = getMyRole(household);
                            const isPrimary = isPrimaryHousehold(household);
                            const isActive = activeHouseholdId === household.id;

                            return (
                                <div 
                                    key={household.id} 
                                    onClick={() => setEditingHouseholdId(household.id)}
                                    className={`bg-white dark:bg-slate-900 border transition-all duration-200 rounded-2xl shadow-sm overflow-hidden p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 flex items-center justify-between gap-3 ${isActive ? 'border-sky-300 dark:border-sky-700 ring-2 ring-sky-50 dark:ring-sky-900/20' : 'border-slate-200 dark:border-slate-700'}`}
                                >
                                    <div className="flex gap-3 min-w-0 items-center">
                                        <div className={`w-1 rounded-full self-stretch min-h-[2.5rem] ${isActive ? 'bg-sky-500' : (myRole === 'owner' ? 'bg-amber-400' : 'bg-slate-300 dark:bg-slate-600')}`} />
                                        <div className="min-w-0">
                                            <h4 className="font-black text-slate-800 dark:text-slate-100 text-base leading-tight truncate">
                                                {getHouseholdName(household)}
                                            </h4>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-1">
                                                <span className={`text-[9px] uppercase font-black px-2 py-0.5 rounded-full flex-shrink-0 ${
                                                    myRole === 'owner' ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 
                                                    myRole === 'admin' ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300' : 
                                                    'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
                                                }`}>
                                                    {tRole(myRole)}
                                                </span>
                                                {isPrimary && (
                                                    <span className="text-[9px] bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-2 py-0.5 rounded-full font-black uppercase tracking-tighter flex-shrink-0">
                                                        {t('primary')}
                                                    </span>
                                                )}
                                                <span className="text-[10px] text-slate-500 dark:text-slate-400 font-bold">
                                                    {household.members.length} {t('members')} • {formatResidentsSummary(household)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1.5 flex-shrink-0" onClick={e => e.stopPropagation()}>
                                        {isActive ? (
                                            <span className="px-3 py-1.5 bg-sky-600 dark:bg-sky-700 text-white rounded-full text-[10px] font-black uppercase tracking-widest shadow-sm">
                                                {t('status_active')}
                                            </span>
                                        ) : (
                                            <button 
                                                onClick={() => onSwitchHousehold(household.id)}
                                                className="px-3 py-1.5 bg-slate-900 dark:bg-slate-700 text-white rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-slate-600 transition-all shadow-sm active:scale-95"
                                            >
                                                {t('select')}
                                            </button>
                                        )}
                                        <button 
                                            onClick={() => setViewingDetails(household)}
                                            className="p-2 text-slate-300 dark:text-slate-600 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-full transition-all"
                                        >
                                            <InformationCircleIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="p-4 bg-slate-100 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end flex-shrink-0">
                    <button onClick={onClose} className="px-8 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all shadow-lg active:scale-98">{t('close')}</button>
                </div>
            </div>

            {/* CREATE HOUSEHOLD MODAL */}
            {isCreateHouseholdOpen && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => { setIsCreateHouseholdOpen(false); resetCreateForm(); }}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden border border-sky-100 dark:border-sky-900 animate-fade-in" onClick={(e) => e.stopPropagation()}>
                        <div className="p-5 border-b bg-sky-50/50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-900 flex justify-between items-center">
                            <h3 className="font-black text-sky-800 dark:text-sky-300 flex items-center gap-2">
                                <PlusIcon className="w-4 h-4" />
                                {t('createNewHousehold')}
                            </h3>
                            <button type="button" onClick={() => { setIsCreateHouseholdOpen(false); resetCreateForm(); }} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-2xl font-bold">&times;</button>
                        </div>
                        <form onSubmit={handleCreate} className="p-5 space-y-4">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('householdName')}</label>
                                <input 
                                    type="text" 
                                    value={newHouseholdName}
                                    onChange={(e) => setNewHouseholdName(e.target.value)}
                                    placeholder={t('householdNamePlaceholder')}
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                                    autoFocus
                                />
                            </div>
                            <div className="flex gap-3">
                                <div className="flex-1 flex flex-col items-center gap-1.5">
                                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('adultsCount')}</label>
                                    <SmartUnitCapsule
                                        value={newAdultsCount}
                                        unit=""
                                        onChange={(v) => setNewAdultsCount(v)}
                                        language={currentUser.language}
                                        step={1}
                                        min={1}
                                        max={99}
                                        hideUnit
                                    />
                                </div>
                                <div className="flex-1 flex flex-col items-center gap-1.5">
                                    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('childrenCount')}</label>
                                    <SmartUnitCapsule
                                        value={newChildrenCount}
                                        unit=""
                                        onChange={(v) => setNewChildrenCount(v)}
                                        language={currentUser.language}
                                        step={1}
                                        min={0}
                                        max={99}
                                        hideUnit
                                    />
                                </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-4 text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-50/50 dark:bg-slate-800/50 p-3 rounded-lg border border-slate-100 dark:border-slate-700">
                                <span className="font-black text-slate-400 dark:text-slate-500 uppercase tracking-tighter">{t('visibleMealsLabel')}</span>
                                {(['Breakfast', 'Lunch', 'Dinner'] as MealType[]).map(meal => (
                                    <label key={meal} className="flex items-center gap-2 cursor-pointer hover:text-sky-600 dark:hover:text-sky-400 transition-colors">
                                        <input 
                                            type="checkbox" 
                                            checked={(newVisibleMeals as any)[meal]} 
                                            onChange={e => setNewVisibleMeals({...newVisibleMeals, [meal]: e.target.checked})} 
                                            className="w-4 h-4 rounded text-sky-600 border-slate-300 dark:border-slate-600 focus:ring-sky-500 bg-white dark:bg-slate-700" 
                                        />
                                        {t(meal === 'Breakfast' ? 'morning' : meal === 'Lunch' ? 'noon' : 'evening')}
                                    </label>
                                ))}
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => { setIsCreateHouseholdOpen(false); resetCreateForm(); }}
                                    className="flex-1 py-3 text-slate-500 dark:text-slate-400 font-bold text-sm"
                                >
                                    {t('cancel')}
                                </button>
                                <button 
                                    type="submit" 
                                    className="flex-[2] bg-slate-900 dark:bg-slate-700 text-white py-3 rounded-xl hover:bg-slate-800 dark:hover:bg-slate-600 font-black transition-all disabled:opacity-50"
                                    disabled={!newHouseholdName.trim()}
                                >
                                    {t('create')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* HOUSEHOLD SETTINGS OVERLAY MODAL */}
            {currentEditingHousehold && (
                <div className="fixed inset-0 z-[105] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setEditingHouseholdId(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto border border-sky-100 dark:border-sky-900 flex flex-col animate-fade-in" onClick={e => e.stopPropagation()}>
                        
                        {/* Settings Modal Header */}
                        <div className="p-5 border-b bg-sky-50/50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-900 flex justify-between items-center flex-shrink-0">
                            <div className="min-w-0">
                                <h3 className="font-black text-sky-800 dark:text-sky-300 flex items-center gap-2 text-base leading-tight truncate">
                                    <CogIcon className="w-5 h-5 shrink-0" />
                                    {getHouseholdName(currentEditingHousehold)}
                                </h3>
                                <div className="flex gap-1.5 mt-1.5">
                                    <span className="bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
                                        {currentEditingHousehold.members.length} {t('members')}
                                    </span>
                                    <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
                                        {getHouseholdAdultsCount(currentEditingHousehold)} {t('adultsCount')}
                                    </span>
                                    {getHouseholdChildrenCount(currentEditingHousehold) > 0 && (
                                        <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[9px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0">
                                            {getHouseholdChildrenCount(currentEditingHousehold)} {t('childrenCount')}
                                        </span>
                                    )}
                                </div>
                            </div>
                            <button type="button" onClick={() => setEditingHouseholdId(null)} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-2xl font-bold p-1">&times;</button>
                        </div>

                        {/* Settings Modal Body */}
                        <div className="p-5 space-y-6 flex-grow overflow-y-auto">
                            {/* Edit Name Block */}
                            {editingRole === 'owner' && (
                                <div className="bg-slate-50/50 dark:bg-slate-850 p-4 rounded-xl border border-slate-100 dark:border-slate-850 space-y-4">
                                    <div>
                                        <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">
                                            {t('householdName')}
                                        </label>
                                        <input
                                            type="text"
                                            value={getHouseholdName(currentEditingHousehold)}
                                            onChange={(e) => handleHouseholdNameChange(currentEditingHousehold.id, e.target.value)}
                                            onBlur={() => handleHouseholdNameSave(currentEditingHousehold)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.currentTarget.blur();
                                                }
                                            }}
                                            className="w-full p-3 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all text-slate-800 dark:text-slate-100"
                                        />
                                    </div>
                                    
                                    {/* Meal types checkboxes */}
                                    <div className="pt-1">
                                        <h5 className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2.5 flex items-center gap-1.5">
                                            <CogIcon className="w-3.5 h-3.5" /> {t('mealSettingsTitle')}
                                        </h5>
                                        <div className="flex gap-4 text-xs font-bold text-slate-700 dark:text-slate-300">
                                            {(['Breakfast', 'Lunch', 'Dinner'] as MealType[]).map(meal => {
                                                const rawMeals = currentEditingHousehold.visibleMealTypes || ['Lunch', 'Dinner'];
                                                const activeMeals: MealType[] = Array.isArray(rawMeals) 
                                                    ? rawMeals 
                                                    : (Object.keys(rawMeals) as MealType[]).filter(k => (rawMeals as any)[k] === true);
                                                return (
                                                    <label key={meal} className="flex items-center gap-2 cursor-pointer hover:text-sky-600 dark:hover:text-sky-400">
                                                        <input 
                                                            type="checkbox" 
                                                            checked={activeMeals.includes(meal)} 
                                                            onChange={() => toggleMealSetting(currentEditingHousehold, meal)}
                                                            className="w-4 h-4 rounded text-sky-600 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-850"
                                                        />
                                                        {t(meal === 'Breakfast' ? 'morning' : meal === 'Lunch' ? 'noon' : 'evening')}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Registered Members Section */}
                            <div className="space-y-2">
                                <button 
                                    type="button"
                                    onClick={() => setExpandedMemberIds(prev => ({ ...prev, [currentEditingHousehold.id]: !prev[currentEditingHousehold.id] }))}
                                    className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm cursor-pointer"
                                >
                                    <div className="flex items-center gap-2">
                                        <UsersIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                        <h4 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                            {t('membersHeader')}
                                        </h4>
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                        <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                            <span className="text-[10px] font-black text-slate-600 dark:text-slate-400">
                                                {currentEditingHousehold.members.length}
                                            </span>
                                        </div>
                                        <div className="text-slate-400 transition-transform duration-200" style={{ transform: expandedMemberIds[currentEditingHousehold.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                            <ChevronDownIcon className="w-4 h-4" />
                                        </div>
                                    </div>
                </button>

                                {expandedMemberIds[currentEditingHousehold.id] && (
                                    <div className="space-y-3 bg-white dark:bg-slate-900 rounded-xl">
                                        <div className="divide-y divide-slate-100 dark:divide-slate-800 border rounded-xl overflow-hidden bg-slate-50/30 dark:bg-slate-900/40">
                                            {[...currentEditingHousehold.members].sort((a, b) => {
                                                const aIsTenant = currentEditingHousehold.memberIsTenant?.[a] !== false;
                                                const bIsTenant = currentEditingHousehold.memberIsTenant?.[b] !== false;
                                                if (aIsTenant && !bIsTenant) return -1;
                                                if (!aIsTenant && bIsTenant) return 1;
                                                return 0;
                                            }).map(uid => {
                                                const mUser = allUsers.find(u => u.uid === uid);
                                                const memberRole = currentEditingHousehold.memberRoles?.[uid] || (uid === currentEditingHousehold.ownerId ? 'owner' : 'viewer');
                                                const isOwner = uid === currentEditingHousehold.ownerId;
                                                const canRemove = canManageMembers(editingRole) && !isOwner && uid !== currentUser.uid;
                                                const canEditRole = canChangeRoles(editingRole) && !isOwner;
                                                
                                                const isExpanded = expandedMemberIds[`member-${uid}`];
                                                const isTenantChecked = currentEditingHousehold.memberIsTenant?.[uid] !== false;
                                                const isChildChecked = currentEditingHousehold.memberIsChild?.[uid] === true;

                                                return (
                                                    <div key={uid} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-all p-3">
                                                        <div 
                                                            onClick={() => setExpandedMemberIds(prev => ({ ...prev, [`member-${uid}`]: !prev[`member-${uid}`] }))}
                                                            className="flex justify-between items-center gap-3 cursor-pointer"
                                                        >
                                                            <div className="min-w-0 flex-1">
                                                                <p className="font-bold text-slate-800 dark:text-slate-100 text-sm truncate flex items-center gap-1.5">
                                                                    {mUser ? (mUser.nickname || `${mUser.firstName || ''} ${mUser.lastName || ''}`.trim() || mUser.email) : t('unknownUser')}
                                                                    {uid === currentUser.uid && (
                                                                        <span className="text-[9px] bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300 px-1.5 py-0.5 rounded font-black uppercase tracking-tighter">
                                                                            {t('me')}
                                                                        </span>
                                                                    )}
                                                                </p>
                                                                
                                                                {/* Badges directly below name (email removed) */}
                                                                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                                                    <span className="text-[9px] font-black uppercase text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">
                                                                        {tRole(memberRole)}
                                                                    </span>
                                                                    <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                                        isTenantChecked ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300' : 'bg-slate-100 dark:bg-slate-850 text-slate-400 dark:text-slate-500'
                                                                    }`}>
                                                                        {isTenantChecked ? t('resident') : t('notTenant')}
                                                                    </span>
                                                                    {isTenantChecked && isChildChecked && (
                                                                        <span className="text-[9px] font-black uppercase bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 rounded">
                                                                            {t('childBadge')}
                                                                        </span>
                                                                    )}
                                                                    {/* Dietary/Allergy existence indicator */}
                                                                    {mUser && ((mUser.userDietTypes && Object.values(mUser.userDietTypes).some(Boolean)) || (mUser.knownAllergies && mUser.knownAllergies.length > 0)) && (
                                                                        <span className="text-[9px] font-black uppercase bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0">
                                                                            <span className="w-1 h-1 bg-emerald-500 rounded-full shrink-0" />
                                                                            {t('dietaryRestrictions')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            <div className="p-2 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 rounded-full transition-colors shrink-0">
                                                                <CogIcon className="w-4 h-4" />
                                                            </div>
                                                        </div>

                                                        {/* Expanded member configurations card */}
                                                        {isExpanded && (
                                                            <div className="mt-3 p-3 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl space-y-3 animate-fade-in">
                                                                {/* Email display with copy button */}
                                                                {mUser?.email && (
                                                                    <div className="flex items-center justify-between bg-slate-50 dark:bg-slate-900 p-2.5 rounded-lg border border-slate-100 dark:border-slate-800 text-xs">
                                                                        <span className="font-mono text-slate-500 dark:text-slate-400 select-all truncate">{mUser.email}</span>
                                                                        <button 
                                                                            type="button"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigator.clipboard.writeText(mUser.email);
                                                                                setInfoNotice({
                                                                                    title: t('copied') || 'Copied!',
                                                                                    message: mUser.email
                                                                                });
                                                                            }}
                                                                            className="text-[10px] font-black uppercase text-sky-600 hover:text-sky-700 bg-sky-50 dark:bg-sky-950/20 px-2.5 py-1 rounded transition-colors shrink-0"
                                                                        >
                                                                            {t('copyBtn') || 'Copy'}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                <div className="flex flex-wrap items-center justify-between gap-3">
                                                                    {/* Tenant / Child checkboxes */}
                                                                    <div className="flex gap-4 text-xs font-bold text-slate-700 dark:text-slate-300">
                                                                        <label className="flex items-center gap-1.5 cursor-pointer">
                                                                            <input 
                                                                                type="checkbox" 
                                                                                checked={isTenantChecked}
                                                                                onChange={(e) => {
                                                                                    const updated = { ...(currentEditingHousehold.memberIsTenant || {}) };
                                                                                    updated[uid] = e.target.checked;
                                                                                    const updatedHh = { ...currentEditingHousehold, memberIsTenant: updated };
                                                                                    onUpdateHouseholdConfig(currentEditingHousehold.id, {
                                                                                        memberIsTenant: updated,
                                                                                        adultsCount: getHouseholdAdultsCount(updatedHh),
                                                                                        childrenCount: getHouseholdChildrenCount(updatedHh),
                                                                                        residentsCount: getHouseholdAdultsCount(updatedHh) + getHouseholdChildrenCount(updatedHh)
                                                                                    });
                                                                                }}
                                                                                className="w-4 h-4 rounded text-sky-600 border-slate-300"
                                                                            />
                                                                            <span>{t('isTenantLabel')}</span>
                                                                        </label>
                                                                        
                                                                        {isTenantChecked && (
                                                                            <label className="flex items-center gap-1.5 cursor-pointer">
                                                                                <input 
                                                                                    type="checkbox" 
                                                                                    checked={isChildChecked}
                                                                                    onChange={(e) => {
                                                                                        const updated = { ...(currentEditingHousehold.memberIsChild || {}) };
                                                                                        updated[uid] = e.target.checked;
                                                                                        const updatedHh = { ...currentEditingHousehold, memberIsChild: updated };
                                                                                        onUpdateHouseholdConfig(currentEditingHousehold.id, {
                                                                                            memberIsChild: updated,
                                                                                            adultsCount: getHouseholdAdultsCount(updatedHh),
                                                                                            childrenCount: getHouseholdChildrenCount(updatedHh),
                                                                                            residentsCount: getHouseholdAdultsCount(updatedHh) + getHouseholdChildrenCount(updatedHh)
                                                                                        });
                                                                                    }}
                                                                                    className="w-4 h-4 rounded text-sky-600 border-slate-300"
                                                                                />
                                                                                <span>{t('isChildLabel')}</span>
                                                                            </label>
                                                                        )}
                                                                    </div>

                                                                    {/* Role selector dropdown */}
                                                                    {canEditRole ? (
                                                                        <select 
                                                                            value={memberRole}
                                                                            onChange={(e) => onUpdateMemberRole(currentEditingHousehold.id, uid, e.target.value as HouseholdRole)}
                                                                            className="text-[11px] font-black uppercase p-1.5 border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 outline-none"
                                                                        >
                                                                            <option value="admin">{t('adminRole')}</option>
                                                                            <option value="editor">{t('editorRole')}</option>
                                                                            <option value="viewer">{t('viewerRole')}</option>
                                                                        </select>
                                                                    ) : (
                                                                        <span className="text-[10px] font-black uppercase px-2 py-1 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-md">
                                                                            {tRole(memberRole)}
                                                                        </span>
                                                                    )}
                                                                </div>

                                                                {/* Member Personal restrictions profile display */}
                                                                {mUser && (
                                                                    <div className="border-t border-slate-100 dark:border-slate-800 pt-2.5 space-y-1.5 text-xs">
                                                                        <p className="font-bold text-slate-500 dark:text-slate-400 uppercase text-[9px] tracking-wide">
                                                                            {t('nutritionProfile')}
                                                                        </p>
                                                                        {mUser.userDietTypes && Object.values(mUser.userDietTypes).some(Boolean) ? (
                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                {Object.entries(mUser.userDietTypes).filter(([_, active]) => active).map(([dietKey]) => (
                                                                                    <span key={dietKey} className="px-2 py-0.5 bg-brand-sage/10 text-brand-sage text-[10px] font-bold rounded-full">
                                                                                        {t(dietKey)}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <p className="text-slate-400 dark:text-slate-500 italic text-[11px]">{t('noPreferences')}</p>
                                                                        )}
                                                                        {mUser.knownAllergies && mUser.knownAllergies.length > 0 && (
                                                                            <div className="flex flex-wrap gap-1 mt-1">
                                                                                {mUser.knownAllergies.map(algId => {
                                                                                    const config = ALLERGY_CONFIG.find(a => a.id === algId);
                                                                                    return (
                                                                                        <span key={algId} className="px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold rounded-full dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-900/50">
                                                                                            {config ? `${config.emoji} ${t(config.labelKey)}` : algId}
                                                                                        </span>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}

                                                                {/* Moved Deletion trash button inside expanded view */}
                                                                {canRemove && (
                                                                    <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
                                                                            <button 
                                                                                type="button"
                                                                                onClick={() => setConfirmAction({
                                                                                    title: t('removeUserConfirm'),
                                                                                    message: '',
                                                                                    onConfirm: () => onRemoveMember(currentEditingHousehold.id, uid)
                                                                                })} 
                                                                                className="flex items-center gap-1 text-red-500 hover:text-red-600 font-bold uppercase tracking-wider text-[10px] px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-950/20 transition-all active:scale-95"
                                                                            >
                                                                                <TrashIcon className="w-3.5 h-3.5" />
                                                                                {t('removeKey') || 'Remove'}
                                                                            </button>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        {/* Invite New User Input */}
                                        {canManageMembers(editingRole) && hasPersonalDetails && (
                                            <div className="flex gap-2 pt-2">
                                                <input 
                                                    type="email" 
                                                    placeholder={t('addUserPlaceholder')}
                                                    className="flex-grow p-3 text-sm bg-slate-50 dark:bg-slate-850 border border-slate-200 dark:border-slate-700 rounded-xl outline-none text-slate-800 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                                                    value={inviteEmailInput}
                                                    onChange={(e) => setInviteEmailInput(e.target.value)}
                                                    dir="ltr"
                                                />
                                                <button 
                                                    type="button"
                                                    onClick={() => handleInviteMember(currentEditingHousehold.id)} 
                                                    className="bg-sky-600 dark:bg-sky-700 text-white px-5 py-3 font-black rounded-xl hover:bg-sky-700 dark:hover:bg-sky-600 transition-all text-sm active:scale-95"
                                                >
                                                    {t('invite') || 'Invite'}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Collapsible Invited Members list */}
                            {canManageMembers(editingRole) && currentEditingHousehold.invitedMembers && currentEditingHousehold.invitedMembers.length > 0 && (
                                <div className="space-y-2">
                                    <button 
                                        type="button"
                                        onClick={() => setIsInvitedExpanded(prev => ({ ...prev, [currentEditingHousehold.id]: !prev[currentEditingHousehold.id] }))}
                                        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            <UsersIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                            <h4 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                                {t('invitedHeader')}
                                            </h4>
                                        </div>

                                        <div className="flex items-center gap-2.5">
                                            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <span className="text-[10px] font-black text-slate-600 dark:text-slate-400">
                                                    {currentEditingHousehold.invitedMembers.length}
                                                </span>
                                            </div>
                                            <div className="text-slate-400 transition-transform duration-200" style={{ transform: isInvitedExpanded[currentEditingHousehold.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                <ChevronDownIcon className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </button>
                                    
                                    {isInvitedExpanded[currentEditingHousehold.id] && (
                                        <div className="border rounded-xl bg-slate-50/20 dark:bg-slate-900/40 divide-y divide-slate-100 dark:divide-slate-850">
                                            {currentEditingHousehold.invitedMembers.map(uid => {
                                                const mUser = allUsers.find(u => u.uid === uid);
                                                return (
                                                    <div key={uid} className="flex items-center justify-between p-3">
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-slate-700 dark:text-slate-300 text-sm truncate">
                                                                {mUser ? (mUser.nickname || `${mUser.firstName || ''} ${mUser.lastName || ''}`.trim() || mUser.email) : t('unknownUser')}
                                                            </p>
                                                            <p className="text-[10px] text-slate-400 dark:text-slate-500">{mUser?.email}</p>
                                                        </div>
                                                        <button 
                                                            type="button"
                                                            onClick={() => handleCancelInvitation(currentEditingHousehold.id, uid)}
                                                            className="text-[10px] font-black uppercase text-red-500 hover:text-red-600 px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-950/20 transition-all"
                                                        >
                                                            {t('cancel')}
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Collapsible Rejected Members list (24-hour retention window) */}
                            {canManageMembers(editingRole) && currentEditingHousehold.rejectedMembers && Object.keys(currentEditingHousehold.rejectedMembers).length > 0 && (
                                <div className="space-y-2">
                                    <button 
                                        type="button"
                                        onClick={() => setIsRejectedExpanded(prev => ({ ...prev, [currentEditingHousehold.id]: !prev[currentEditingHousehold.id] }))}
                                        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            <TrashIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                            <h4 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                                {t('rejectedHeader')}
                                            </h4>
                                        </div>

                                        <div className="flex items-center gap-2.5">
                                            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <span className="text-[10px] font-black text-slate-600 dark:text-slate-400">
                                                    {Object.entries(currentEditingHousehold.rejectedMembers).filter(([_, ts]) => Date.now() - ts <= 24 * 60 * 60 * 1000).length}
                                                </span>
                                            </div>
                                            <div className="text-slate-400 transition-transform duration-200" style={{ transform: isRejectedExpanded[currentEditingHousehold.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                <ChevronDownIcon className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </button>

                                    {isRejectedExpanded[currentEditingHousehold.id] && (
                                        <div className="border rounded-xl bg-slate-50/20 dark:bg-slate-900/40 divide-y divide-slate-100 dark:divide-slate-850">
                                            {Object.entries(currentEditingHousehold.rejectedMembers)
                                                .filter(([_, ts]) => Date.now() - ts <= 24 * 60 * 60 * 1000)
                                                .map(([uid]) => {
                                                    const mUser = allUsers.find(u => u.uid === uid);
                                                    return (
                                                        <div key={uid} className="flex items-center justify-between p-3">
                                                            <div className="min-w-0">
                                                                <p className="font-bold text-slate-700 dark:text-slate-300 text-sm truncate">
                                                                    {mUser ? (mUser.nickname || `${mUser.firstName || ''} ${mUser.lastName || ''}`.trim() || mUser.email) : t('unknownUser')}
                                                                </p>
                                                                <p className="text-[10px] text-slate-400 dark:text-slate-500">{mUser?.email}</p>
                                                            </div>
                                                            <button 
                                                                type="button"
                                                                onClick={() => handleClearRejected(currentEditingHousehold.id, uid)}
                                                                className="text-[10px] font-black uppercase text-slate-500 hover:text-slate-700 px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 transition-all"
                                                            >
                                                                {t('clear') || 'Clear'}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Additional Tenants List (Collapsible, managed via separate sub-modal) */}
                            <div className="space-y-2">
                                <button 
                                    type="button"
                                    onClick={() => setIsTenantsExpanded(prev => ({ ...prev, [currentEditingHousehold.id]: !prev[currentEditingHousehold.id] }))}
                                    className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm cursor-pointer"
                                >
                                    <div className="flex items-center gap-2">
                                        <UsersIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                        <h4 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                            {t('residentsHeader')}
                                        </h4>
                                    </div>

                                    <div className="flex items-center gap-2.5">
                                        <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                            <span className="text-[10px] font-black text-slate-600 dark:text-slate-400">
                                                {(currentEditingHousehold.tenants || []).length}
                                            </span>
                                        </div>
                                        <div className="text-slate-400 transition-transform duration-200" style={{ transform: isTenantsExpanded[currentEditingHousehold.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                            <ChevronDownIcon className="w-4 h-4" />
                                        </div>
                                    </div>
                                </button>

                                {isTenantsExpanded[currentEditingHousehold.id] && (
                                    <div className="space-y-3 bg-white dark:bg-slate-900 rounded-xl">
                                        {(currentEditingHousehold.tenants || []).length === 0 ? (
                                            <p className="text-xs text-slate-400 dark:text-slate-500 italic p-3 text-center bg-slate-50/50 dark:bg-slate-850 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                                                {t('noTenants')}
                                            </p>
                                        ) : (
                                            <div className="border rounded-xl bg-slate-50/30 dark:bg-slate-900/40 divide-y divide-slate-100 dark:divide-slate-800">
                                                {(currentEditingHousehold.tenants || []).map(tenant => {
                                                    const isExpanded = expandedTenantIds[tenant.id];
                                                    const selectedDiets = tenant.dietTypes ? Object.entries(tenant.dietTypes).filter(([_, active]) => active).map(([k]) => k) : [];
                                                    return (
                                                        <div key={tenant.id} className="p-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/20 transition-all">
                                                            <div 
                                                                onClick={() => setExpandedTenantIds(prev => ({ ...prev, [tenant.id]: !prev[tenant.id] }))}
                                                                className="flex justify-between items-center gap-3 cursor-pointer"
                                                            >
                                                                <div className="min-w-0 flex-grow">
                                                                    <p className="font-bold text-slate-800 dark:text-slate-100 text-sm truncate flex items-center gap-1.5">
                                                                        {tenant.name}
                                                                        <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                                            tenant.isChild ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300' : 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300'
                                                                        }`}>
                                                                            {tenant.isChild ? t('childBadge') : t('adultBadge')}
                                                                        </span>
                                                                    </p>
                                                                    {/* Dietary/Allergy existence indicator */}
                                                                    {((tenant.dietTypes && Object.values(tenant.dietTypes).some(Boolean)) || (tenant.knownAllergies && tenant.knownAllergies.length > 0)) && (
                                                                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                                                                            <span className="text-[9px] font-black uppercase bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded flex items-center gap-1 flex-shrink-0">
                                                                                <span className="w-1 h-1 bg-emerald-500 rounded-full shrink-0" />
                                                                                {t('dietaryRestrictions')}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                <div className="flex items-center gap-1.5 shrink-0" onClick={e => e.stopPropagation()}>
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => setExpandedTenantIds(prev => ({ ...prev, [tenant.id]: !prev[tenant.id] }))}
                                                                        className="p-1.5 text-slate-400 hover:text-slate-600 dark:text-slate-500 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors text-[10px] font-bold uppercase tracking-wider shrink-0"
                                                                    >
                                                                        {isExpanded ? t('hideDetails') : t('showDetails')}
                                                                    </button>
                                                                    {canManageMembers(editingRole) && (
                                                                        <button 
                                                                            type="button"
                                                                            onClick={() => handleEditTenantClick(currentEditingHousehold.id, tenant)}
                                                                            className="p-2 text-slate-400 hover:text-sky-600 dark:text-slate-500 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full transition-colors shrink-0"
                                                                            aria-label="Edit Tenant"
                                                                        >
                                                                            <CogIcon className="w-4 h-4" />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            
                                                            {/* Expanded details cards display */}
                                                            {isExpanded && (
                                                                <div className="mt-3 p-3 bg-white dark:bg-slate-850 border border-slate-200 dark:border-slate-800 rounded-xl space-y-3 text-xs text-slate-600 dark:text-slate-300 animate-fade-in">
                                                                    {/* Diets Section */}
                                                                    <div className="space-y-1">
                                                                        <span className="font-bold text-[9px] uppercase tracking-wide text-slate-400">{t('nutritionProfile')}</span>
                                                                        {selectedDiets.length > 0 ? (
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {selectedDiets.map(dKey => (
                                                                                    <span key={dKey} className="px-2 py-0.5 bg-brand-sage/10 text-brand-sage text-[10px] font-bold rounded-full font-sans">
                                                                                        {t(dKey)}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        ) : (
                                                                            <p className="italic text-[10px] text-slate-400">{t('noPreferences')}</p>
                                                                        )}
                                                                    </div>

                                                                    {/* Allergies Section */}
                                                                    <div className="space-y-1 pt-2 border-t border-slate-100 dark:border-slate-800/60">
                                                                        <span className="font-bold text-[9px] uppercase tracking-wide text-slate-400">{t('knownAllergies')}</span>
                                                                        {tenant.knownAllergies && tenant.knownAllergies.length > 0 ? (
                                                                            <div className="flex flex-wrap gap-1">
                                                                                {tenant.knownAllergies.map(algId => {
                                                                                    const config = ALLERGY_CONFIG.find(a => a.id === algId);
                                                                                    return (
                                                                                        <span key={algId} className="px-2 py-0.5 bg-amber-50 text-amber-800 border border-amber-200 text-[10px] font-bold rounded-full dark:bg-amber-900/20 dark:text-amber-200 dark:border-amber-900/50">
                                                                                            {config ? `${config.emoji} ${t(config.labelKey)}` : algId}
                                                                                        </span>
                                                                                    );
                                                                                })}
                                                                            </div>
                                                                        ) : (
                                                                            <p className="italic text-[10px] text-slate-400">{t('noPreferences')}</p>
                                                                        )}
                                                                    </div>

                                                                    {canManageMembers(editingRole) && (
                                                                        <div className="flex justify-end pt-2 border-t border-slate-100 dark:border-slate-800">
                                                                            <button 
                                                                                type="button"
                                                                                onClick={() => setConfirmAction({
                                                                                    title: t('deleteTenant') || 'Delete Tenant',
                                                                                    message: tenant.name,
                                                                                    onConfirm: () => handleDeleteTenant(currentEditingHousehold.id, tenant.id)
                                                                                })}
                                                                                className="flex items-center gap-1 text-red-500 hover:text-red-600 font-bold uppercase tracking-wider text-[10px] px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-950/20 transition-all active:scale-95"
                                                                            >
                                                                                <TrashIcon className="w-3.5 h-3.5" />
                                                                                {t('removeKey') || 'Remove'}
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {/* Add Tenant Button condition: only visible if expanded OR list is empty */}
                                        {canManageMembers(editingRole) && (isTenantsExpanded[currentEditingHousehold.id] || (currentEditingHousehold.tenants || []).length === 0) && (
                                            <div className="pt-2">
                                                <button 
                                                    type="button"
                                                    onClick={() => handleAddTenantClick(currentEditingHousehold.id)}
                                                    className="w-full py-2.5 border border-dashed border-sky-300 dark:border-sky-700 hover:bg-sky-50/50 dark:hover:bg-sky-950/20 text-sky-600 dark:text-sky-400 font-bold rounded-xl text-xs flex items-center justify-center gap-1.5 transition-all"
                                                >
                                                    <PlusIcon className="w-3.5 h-3.5" />
                                                    {t('addTenant')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Collapsible Temporary Users list */}
                            {canManageMembers(editingRole) && (
                                <div className="space-y-2">
                                    <button 
                                        type="button"
                                        onClick={() => setIsTempUsersExpanded(prev => ({ ...prev, [currentEditingHousehold.id]: !prev[currentEditingHousehold.id] }))}
                                        className="w-full flex items-center justify-between px-5 py-3 bg-slate-50 dark:bg-slate-800/40 hover:bg-slate-100 dark:hover:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-2xl transition-all shadow-sm cursor-pointer"
                                    >
                                        <div className="flex items-center gap-2">
                                            <UsersIcon className="w-4 h-4 text-slate-400 shrink-0" />
                                            <h4 className="text-sm font-black uppercase tracking-wider text-slate-700 dark:text-slate-300">
                                                {t('tempUsers') || 'Temporary Users'}
                                            </h4>
                                        </div>

                                        <div className="flex items-center gap-2.5">
                                            <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
                                                <span className="text-[10px] font-black text-slate-600 dark:text-slate-400">
                                                    {tempAccessRows.length}
                                                </span>
                                            </div>
                                            <div className="text-slate-400 transition-transform duration-200" style={{ transform: isTempUsersExpanded[currentEditingHousehold.id] ? 'rotate(180deg)' : 'rotate(0deg)' }}>
                                                <ChevronDownIcon className="w-4 h-4" />
                                            </div>
                                        </div>
                                    </button>

                                    {isTempUsersExpanded[currentEditingHousehold.id] && (
                                        <div className="space-y-3 p-3 border border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/10 dark:bg-slate-900/10 animate-fade-in">
                                            {/* List of existing temp users */}
                                            {tempAccessRows.length > 0 && (
                                                <div className="border rounded-xl bg-white dark:bg-slate-900 divide-y divide-slate-100 dark:divide-slate-800">
                                                    {tempAccessRows.map((row) => {
                                                        const client = tempClients[row.clientId];
                                                        const name = client ? `${client.firstName} ${client.lastName || ''}`.trim() : 'Loading...';
                                                        const email = client?.email || '';
                                                        return (
                                                            <div key={row.id} className="p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:bg-slate-50/50 dark:hover:bg-slate-800/10 transition-all">
                                                                <div className="min-w-0">
                                                                    <p className="font-bold text-slate-800 dark:text-slate-100 text-sm truncate flex items-center gap-1.5">
                                                                        {name}
                                                                        <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded ${
                                                                            row.isActive ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500'
                                                                        }`}>
                                                                            {row.isActive ? t('active') : t('inactive')}
                                                                        </span>
                                                                    </p>
                                                                    <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">{email}</p>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 flex-wrap">
                                                                    {row.isActive && client && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRefreshTempLink(row.id)}
                                                                            className="text-[10px] font-black uppercase text-sky-600 hover:text-sky-700 bg-sky-50 dark:bg-sky-950/20 px-2.5 py-1 rounded-lg active:scale-95 transition-all"
                                                                        >
                                                                            {t('refreshLink') || 'Refresh Link'}
                                                                        </button>
                                                                    )}
                                                                    <button 
                                                                        type="button"
                                                                        onClick={() => setConfirmAction({
                                                                            title: t('addTempUser') || 'Remove Temporary User',
                                                                            message: t('deleteTempUserConfirm') || 'Are you sure you want to remove this temporary user?',
                                                                            onConfirm: () => handleDeleteTempUser(row.id, row.clientId)
                                                                        })}
                                                                        className="flex items-center gap-1 text-red-500 hover:text-red-600 font-bold uppercase tracking-wider text-[10px] px-2.5 py-1.5 rounded-lg bg-red-50 dark:bg-red-950/20 transition-all active:scale-95"
                                                                    >
                                                                        <TrashIcon className="w-3.5 h-3.5" />
                                                                        {t('removeKey') || 'Remove'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {/* Add Temp User Form */}
                                            <form onSubmit={handleAddTempUser} className="space-y-3 bg-white dark:bg-slate-900 p-4 border border-slate-100 dark:border-slate-800 rounded-xl">
                                                <h5 className="text-[11px] font-black uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                                    {t('addTempUser') || 'Add Temporary User'}
                                                </h5>
                                                
                                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">{t('firstName')}</label>
                                                        <input 
                                                            type="text" 
                                                            required
                                                            value={tempFirstName}
                                                            onChange={e => setTempFirstName(e.target.value)}
                                                            className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:border-sky-500 focus:bg-white outline-none transition-all"
                                                            placeholder={t('firstName')}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">{t('lastName')}</label>
                                                        <input 
                                                            type="text" 
                                                            value={tempLastName}
                                                            onChange={e => setTempLastName(e.target.value)}
                                                            className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:border-sky-500 focus:bg-white outline-none transition-all"
                                                            placeholder={t('lastName') || 'Last Name'}
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase mb-1">{t('email')}</label>
                                                    <input 
                                                        type="email" 
                                                        required
                                                        value={tempEmail}
                                                        onChange={e => setTempEmail(e.target.value)}
                                                        className="w-full px-3 py-2 text-xs border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 focus:border-sky-500 focus:bg-white outline-none transition-all dir-ltr text-start"
                                                        placeholder="temp@example.com"
                                                    />
                                                </div>

                                                {tempError && <p className="text-[11px] text-red-500 font-bold">{tempError}</p>}

                                                <div className="flex justify-end pt-1">
                                                    <button
                                                        type="submit"
                                                        disabled={tempLoading}
                                                        className="px-4 py-2 bg-brand-orange text-white text-xs font-bold rounded-xl hover:bg-orange-600 transition-colors shadow disabled:opacity-50 flex items-center gap-1 active:scale-95"
                                                    >
                                                        <PlusIcon className="w-3.5 h-3.5" />
                                                        {tempLoading ? t('loading') : (t('addTempUser') || 'Add')}
                                                    </button>
                                                </div>
                                            </form>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Household Delete Option */}
                            {editingRole === 'owner' && !isPrimaryHousehold(currentEditingHousehold) && (
                                <div className="pt-5 border-t border-slate-100 dark:border-slate-800 flex justify-end">
                                    <button 
                                        type="button"
                                        onClick={() => setConfirmAction({
                                            title: t('deleteHousehold'),
                                            message: currentEditingHousehold.name,
                                            onConfirm: () => {
                                                onDeleteHousehold(currentEditingHousehold.id);
                                                setEditingHouseholdId(null);
                                            }
                                        })} 
                                        className="flex items-center gap-1.5 text-red-500 hover:text-red-600 font-bold uppercase tracking-wider text-[10px] px-3 py-2 rounded-xl bg-red-50 dark:bg-red-950/20 transition-all active:scale-95"
                                    >
                                        <TrashIcon className="w-4 h-4" />
                                        {t('deleteHousehold')}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Settings Modal Footer */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end flex-shrink-0">
                            <button 
                                type="button" 
                                onClick={() => setEditingHouseholdId(null)} 
                                className="px-6 py-2 bg-slate-800 dark:bg-slate-700 text-white font-bold rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-colors shadow text-xs active:scale-98"
                            >
                                {t('close')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* DEDICATED TENANT CONFIGURATION WINDOW SUB-MODAL */}
            {activeTenantForm && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setActiveTenantForm(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border border-sky-100 dark:border-sky-900 flex flex-col animate-fade-in" onClick={e => e.stopPropagation()}>
                        
                        {/* Tenant Modal Header */}
                        <div className="p-5 border-b bg-sky-50/50 dark:bg-sky-900/20 border-sky-100 dark:border-sky-900 flex justify-between items-center flex-shrink-0">
                            <h3 className="font-black text-sky-800 dark:text-sky-300 flex items-center gap-2">
                                <CogIcon className="w-5 h-5 shrink-0" />
                                {activeTenantForm.tenant ? t('editTenant') : t('addTenant')}
                            </h3>
                            <button type="button" onClick={() => setActiveTenantForm(null)} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-2xl font-bold p-1">&times;</button>
                        </div>

                        {/* Tenant Modal Body */}
                        <div className="p-5 space-y-4 flex-grow overflow-y-auto">
                            <div>
                                <label className="block text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1.5 px-1">{t('tenantName')}</label>
                                <input 
                                    type="text" 
                                    value={tenantNameInput}
                                    onChange={(e) => setTenantNameInput(e.target.value)}
                                    placeholder={t('tenantName')}
                                    className="w-full p-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-sky-500 outline-none transition-all text-slate-800 dark:text-slate-100 placeholder:text-slate-400"
                                    autoFocus
                                />
                            </div>

                            {/* Child / Adult Switch Selector */}
                            <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-100 dark:border-slate-850 bg-slate-50/30 dark:bg-slate-900/20">
                                <div className="min-w-0">
                                    <p className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                        {t('isChildLabel')}
                                    </p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                        {tenantIsChild ? t('childBadge') : t('adultBadge')}
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={tenantIsChild}
                                    onClick={() => setTenantIsChild(prev => !prev)}
                                    className={`relative inline-flex h-7 w-12 shrink-0 rounded-full transition-colors ${
                                        tenantIsChild ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-700'
                                    }`}
                                >
                                    <span
                                        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform mt-1 ${
                                            tenantIsChild ? 'ltr:translate-x-6 rtl:-translate-x-6' : 'ltr:translate-x-1 ltr:translate-x-1'
                                        }`}
                                    />
                                </button>
                            </div>

                            {/* Collapsible Diet Types Selector */}
                            <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setIsFormDietOpen(prev => !prev)}
                                    className="w-full flex items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 transition-colors text-start"
                                    aria-expanded={isFormDietOpen}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                            {t('dietLabels')}
                                        </p>
                                        {!isFormDietOpen && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                {Object.values(tenantDietTypes).filter(Boolean).length > 0
                                                    ? `${Object.values(tenantDietTypes).filter(Boolean).length} ${t('selected')}`
                                                    : t('nothingSelected')}
                                            </p>
                                        )}
                                    </div>
                                    <ChevronDownIcon
                                        className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${isFormDietOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isFormDietOpen && (
                                    <div className="px-4 pb-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                        <DietTypesSelector
                                            value={tenantDietTypes}
                                            onChange={setTenantDietTypes}
                                            language={currentUser.language}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Collapsible Known Allergies Selector */}
                            <div className="rounded-xl border border-slate-100 dark:border-slate-800 overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => setIsFormAllergiesOpen(prev => !prev)}
                                    className="w-full flex items-center justify-between gap-3 p-4 bg-slate-50/80 dark:bg-slate-800/40 hover:bg-slate-100/80 dark:hover:bg-slate-800/60 transition-colors text-start"
                                    aria-expanded={isFormAllergiesOpen}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                                            {t('knownAllergies')}
                                        </p>
                                        {!isFormAllergiesOpen && (
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                {tenantAllergies.length > 0
                                                    ? `${tenantAllergies.length} ${t('selected')}`
                                                    : t('nothingSelected')}
                                            </p>
                                        )}
                                    </div>
                                    <ChevronDownIcon
                                        className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-200 ${isFormAllergiesOpen ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {isFormAllergiesOpen && (
                                    <div className="px-4 pb-4 pt-3 border-t border-slate-100 dark:border-slate-800">
                                        <KnownAllergiesSelector
                                            value={tenantAllergies}
                                            onChange={setTenantAllergies}
                                            language={currentUser.language}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Tenant Modal Footer */}
                        <div className="p-4 bg-slate-100 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-end gap-3 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => setActiveTenantForm(null)}
                                className="px-6 py-2.5 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl hover:opacity-90 transition-all active:scale-98"
                            >
                                {t('cancel') || 'Cancel'}
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSaveTenant(activeTenantForm.householdId)}
                                disabled={!tenantNameInput.trim()}
                                className="px-8 py-2.5 bg-sky-600 dark:bg-sky-700 text-white text-xs font-black uppercase tracking-wider rounded-xl hover:bg-sky-700 dark:hover:bg-sky-600 transition-all disabled:opacity-50 active:scale-98"
                            >
                                {t('save') || 'Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* viewingDetails dialog notice modal */}
            {viewingDetails && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={() => setViewingDetails(null)}>
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden border border-slate-100 dark:border-slate-700 animate-fade-in" onClick={e => e.stopPropagation()}>
                        <div className="p-5 border-b bg-slate-50 dark:bg-slate-800 border-slate-100 dark:border-slate-700 flex justify-between items-center">
                            <h4 className="font-black text-slate-800 dark:text-slate-100 uppercase tracking-tight flex items-center gap-2 text-sm">
                                <InformationCircleIcon className="w-5 h-5 text-sky-600 dark:text-sky-400 shrink-0" />
                                {t('householdInfo')}
                            </h4>
                            <button onClick={() => setViewingDetails(null)} className="text-slate-400 hover:text-slate-800 dark:text-slate-500 dark:hover:text-slate-300 text-2xl font-bold p-1">&times;</button>
                        </div>
                        <div className="p-6 space-y-4">
                            <div>
                                <h5 className="font-black text-slate-800 dark:text-slate-100 text-base mb-1">{viewingDetails.name}</h5>
                                <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">{t('members')} {viewingDetails.members.length} • {formatResidentsSummary(viewingDetails)}</p>
                            </div>
                            <div className="space-y-1.5">
                                <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">{t('idLabel')}</label>
                                <div className="flex items-center gap-2 bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl border border-slate-100 dark:border-slate-750">
                                    <code className="text-xs font-mono text-slate-600 dark:text-slate-300 break-all select-all flex-grow">{viewingDetails.id}</code>
                                    <button 
                                        onClick={() => {
                                            navigator.clipboard.writeText(viewingDetails.id);
                                            setShowCopiedNotice(true);
                                        }}
                                        className="text-[10px] font-black text-sky-600 dark:text-sky-400 hover:text-sky-800 dark:hover:text-sky-300 px-2 py-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded shadow-sm transition-all"
                                        title={t('copyId')}
                                    >
                                        {t('copyBtn')}
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="p-4 bg-slate-50 dark:bg-slate-800 border-t dark:border-slate-700 flex justify-center">
                            <button onClick={() => setViewingDetails(null)} className="px-10 py-2.5 bg-slate-800 dark:bg-slate-700 text-white font-black rounded-xl hover:bg-slate-900 dark:hover:bg-slate-600 transition-all text-xs">{t('close')}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Standard Confirmation modal */}
            {confirmAction && (
                <ConfirmationModal 
                    isOpen={!!confirmAction}
                    title={confirmAction.title}
                    message={confirmAction.message}
                    confirmText={t('ok')}
                    cancelText={t('cancel')}
                    onConfirm={() => {
                        confirmAction.onConfirm();
                        setConfirmAction(null);
                    }}
                    onCancel={() => setConfirmAction(null)}
                />
            )}

            {/* Standard Info/Notice modal */}
            {infoNotice && (
                <InfoNoticeModal
                    isOpen={!!infoNotice}
                    onClose={() => setInfoNotice(null)}
                    title={infoNotice.title}
                    message={infoNotice.message}
                    variant={infoNotice.variant || 'info'}
                    language={currentUser.language}
                />
            )}

            <CopiedNoticeModal
                isOpen={showCopiedNotice}
                onClose={() => setShowCopiedNotice(false)}
                messageKey="idCopied"
                language={currentUser.language}
            />
        </div>
    );
};

export default HouseholdManagementModal;
